import { observer } from 'mobx-react-lite'
import { useCallback, useMemo, useState } from 'react'
import { Platform, StyleSheet, View } from 'react-native'

import { DSColor, Palette } from '@ppwr/poker'

import { useStore } from '../../../../../models'
import { events } from '../../../../../models/analytics/analytics-events'
import ChatTextInput from './partials/ChatTextInput'
import SendMessageButton from './partials/SendMessageButton'
import { useThrottledCallback } from 'use-debounce'
import { useCommandsContext, useCommonCommands } from '../../../../../screens/new-design/hooks/useCommonCommands'
import { ChatKind } from '../../../../../models/api-store/ChatKindEnum'
import { useGameState } from '../../../../../screens/GameContainer/useGameState'
import { DELAY_TO_SHOW_TYPING_INDICATOR } from './ChatConstants'

export interface ChatsProps {
  onInputFocus: () => void
  onInputBlur: () => void
}

export const ChatFooter = observer(({ onInputFocus, onInputBlur }: ChatsProps) => {
  const {
    analytics,
    session: { user },
    game: { players: legacyPlayers }
  } = useStore()

  const context = useCommandsContext()
  const { players } = useGameState()
  const onSendChat = useCommonCommands('onSendChat', {})
  const [messageValue, setMessageValue] = useState('')

  const trimmedMessage = messageValue.trim()
  const canSendCurrentMessage = trimmedMessage.length > 0

  const defaultPlayerColor: DSColor = 'Brand/Light Grey'
  const playerColor = useMemo(() => {
    if ('legacy' === context) {
      const currentPlayer = legacyPlayers.find((u) => u.userId === user?._id)
      return currentPlayer?.color || defaultPlayerColor
    }
    // TODO come back and decide how to fix for convex or abstract for both in a hook
    // const currentPlayer = players.find((u) => u.userId === user?._id)
    // return currentPlayer?.color || defaultPlayerColor
    return defaultPlayerColor
  }, [legacyPlayers, players, context, user])

  const color = Palette[playerColor]

  const sendIsTyping = useThrottledCallback(
    () => {
      const isTyping = true
      'legacy' === context && onSendChat('Typing', ChatKind.user, Palette[playerColor], isTyping)
    },
    DELAY_TO_SHOW_TYPING_INDICATOR,
    { leading: true }
  )

  const onChangeText = useCallback(
    (text: string) => {
      setMessageValue(text) // was setMessageValue(()=> text), important?
      context === 'legacy' && sendIsTyping()
    },
    [context, sendIsTyping]
  )

  const onFocus = useCallback(() => {
    onInputFocus?.()
  }, [onInputFocus])

  const onBlur = useCallback(() => {
    onInputBlur?.()
  }, [onInputBlur])

  const onSendMessage = useThrottledCallback(
    () => {
      const trimmed = messageValue.trim()
      if (trimmed.length === 0) return
      try {
        onSendChat(trimmed, ChatKind.user, color)
        analytics.setEvent(events.homescreen.chat_sent)
        setMessageValue('')
      } catch (error) {
        console.error('Chat.onSendMessage:', error)
      }
    },
    250,
    { leading: true }
  )

  return (
    <View style={[inputStyles.container, Platform.OS === 'web' && { paddingBottom: 12 }]}>
      <ChatTextInput
        placeholderTextColor="#B2AFA9"
        placeholder="Send message"
        onChangeText={onChangeText}
        value={messageValue}
        onFocus={onFocus}
        onBlur={onBlur}
        onSubmitEditing={onSendMessage}
        returnKeyType="send"
        blurOnSubmit={false}
      />
      <SendMessageButton
        onPress={onSendMessage}
        disabled={!canSendCurrentMessage}
        style={[inputStyles.sendButton, !canSendCurrentMessage && inputStyles.sendButtonDisabled]}
      />
    </View>
  )
})

const inputStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    gap: 8
  },
  sendButton: {
    marginLeft: -52
  },
  sendButtonDisabled: {
    opacity: 0.4
  }
})
